import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { OnGroupDataMessageArgs, WebPubSubClient } from '@azure/web-pubsub-client';
import { Observable, ReplaySubject, BehaviorSubject, firstValueFrom } from 'rxjs';
import { Constants } from 'projects/reg-hub-admin/src/constants';
import { EnvironmentUrlService } from '../../environment-url/environment-url.service';
import { OrderMessage, CANCEL_ON_NAVIGATION } from 'reg-hub-common';

const START_RETRY_DELAY_MS = 10000;

@Injectable({
  providedIn: 'root'
})
export class OrderMessagesHubService implements OnDestroy {
  private client: WebPubSubClient | undefined;
  private messagesSubject = new ReplaySubject<any>(1);
  public messages$: Observable<any> = this.messagesSubject.asObservable();

  private messagesCount = 'MessagesCount';

  // BehaviorSubjects to store orders
  private unreadMessageCountSource = new BehaviorSubject<number>(0);
  unreadMessageCount$ = this.unreadMessageCountSource.asObservable();

  constructor(private http: HttpClient,
    private environmentUrl: EnvironmentUrlService
  ) { }

  connect(): void {
    // The client is created synchronously so a second connect(), even in the same tick, is a no-op
    // rather than a second negotiate and an orphaned connection
    if (this.client) {
      return;
    }

    this.client = new WebPubSubClient({
      // Negotiate on every (re)connect since access URLs expire
      getClientAccessUrl: async () => {
        const response = await firstValueFrom(
          this.http.get<{ url: string }>(`${this.environmentUrl.urlAddress}${Constants.orderMessagesHubNegotiateUrl}`, { context: new HttpContext().set(CANCEL_ON_NAVIGATION, false) }));
        return response.url;
      }
    }, {
      // Each reconnect attempt negotiates with the API, so back off instead of retrying every second
      reconnectRetryOptions: { mode: "Exponential" }
    });

    // Set up event handlers
    this.client.on("connected", (e) => {
      console.log("Connected to Order Messages hub:", e.connectionId);
    });

    // The API joins the connection to the admins group when issuing the URL and only sends messages to that group
    this.client.on("group-message", (messageEvent) => {
      this.handleMessageReceived(messageEvent);
    });

    // handle disconnection from the hub
    this.client.on("disconnected", () => {
      console.warn("Disconnected from Web PubSub");
    });

    this.start();
  }

  // Once started, the client reconnects by itself, but a failed start (e.g. the negotiate fails) is never retried
  private start() {
    this.client?.start()
      .catch(err => {
        console.error('Error starting client', err);
        setTimeout(() => this.start(), START_RETRY_DELAY_MS);
      });
  }

  handleMessageReceived(messageEvent: OnGroupDataMessageArgs) {
    if (messageEvent.message.dataType === "json") {
      const rawMessage = messageEvent.message.data;
      const message: OrderMessage = this.convertKeysToLowercase<OrderMessage>(rawMessage);
      this.updateUnreadMessageCount(message);
      this.messagesSubject.next(message);
    }
  }

  setInitialUnreadMessageCount(count: number) {
    this.unreadMessageCountSource.next(count);
  }

  private updateUnreadMessageCount(message: OrderMessage) {
    var currentCount = this.unreadMessageCountSource.value;
    if (!message.isInternalToCustomer) {
      if (message.isReadByAdmin) {
        currentCount -= 1;
      } else {
        currentCount += 1;
      }
    }
    this.unreadMessageCountSource.next(currentCount);
  }

  ngOnDestroy() {
    // Stop the connection if the service is destroyed (e.g., in certain testing scenarios)
    this.client?.stop();
    this.client = undefined;
  }

  private convertKeysToLowercase<T>(input: any): T {
    const transformedObject: any = {};
    for (const key of Object.keys(input)) {
      transformedObject[key.charAt(0).toLowerCase() + key.slice(1)] = input[key];
    }
    return transformedObject as T;
  }
}
